import { useState } from 'react';
import { toast } from 'sonner';
import { RefreshCwIcon } from 'lucide-react';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchIntegrations, refreshIntegration } from '../adminApi';
import { useCan } from '../useCan';
import { integrationInfo } from '../../data/insightIntegrations';
import { errorMessage } from '../../core/errors';
import { dayShort } from '../../core/format/dates';
import { btnGhost } from '../ui/styles';
import type { IntegrationId, IntegrationStatus } from '../../core/types/insights';
const order: IntegrationId[] = ['umami', 'searchConsole', 'googleBusiness', 'instagram', 'brevo'];
const stateText = (s: IntegrationStatus) => s.status === 'niet-gekoppeld' ? 'Niet gekoppeld' : s.status === 'fout' ? 'Ophalen mislukt' : s.updatedAt ? `Bijgewerkt tot ${dayShort(s.updatedAt.slice(0, 10))}, ${s.updatedAt.slice(11, 16)}` : 'Gekoppeld';

/**
 * De externe bronnen in één regel: gekoppeld, tot wanneer bijgewerkt, en "Opnieuw ophalen".
 * De server haalt elke nacht op; dit zet een extra ophaalronde in de wachtrij.
 */
export function IntegrationStatusBar() {
  // Koppelingen beheren is een eigen bevoegdheid (Marketing heeft hem niet): dan geen verzoek dat 403 geeft.
  const {
    canAll
  } = useCan();
  return canAll('integrations.manage') ? <IntegrationStatusList data-id="element-1875" data-mp="339m2tusxvj0t" /> : null;
}
function IntegrationStatusList() {
  const {
    data,
    setData,
    status
  } = useLatestLoad(fetchIntegrations);
  const [busy, setBusy] = useState<IntegrationId | null>(null);
  const refresh = async (id: IntegrationId) => {
    if (busy) return;
    setBusy(id);
    try {
      const next = await refreshIntegration(id);
      setData(d => d ? {
        ...d,
        [id]: next
      } : d);
      toast.success(`${integrationInfo[id].name}: opnieuw opgehaald`);
    } catch (err) {
      toast.error('Opnieuw ophalen lukte niet', {
        description: errorMessage(err)
      });
    } finally {
      setBusy(null);
    }
  };
  if (!data) return status === 'error' ? <p className="text-[13px] text-muted" data-id="element-1876" data-mp="3vwonbj0s5t82">De status van de koppelingen kon niet laden.</p> : null;
  return <details className="group rounded-lg border border-line bg-white" data-id="element-1877" data-mp="1vycw2fwigg8h">
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3 text-[13px] sm:px-5" data-id="element-1878" data-mp="1rtwugqtxjuz2">
        <span className="text-ink" data-id="element-1879" data-mp="25s3i7e560el0">Koppelingen</span>
        {order.map(id => <span key={id} className="inline-flex items-center gap-1.5 text-muted" data-id="element-1880" data-mp="20w5zj16fiw52">
            <span className={`h-1.5 w-1.5 rounded-full ${data[id].status === 'gekoppeld' ? 'bg-sage' : data[id].status === 'fout' ? 'bg-clay' : 'border border-muted'}`} aria-hidden="true" data-id="element-1881" data-mp="1d6k0qetyyixl" />
            {integrationInfo[id].name.replace(/ \(.*\)$/, '')}
            <span className="sr-only" data-id="element-1882" data-mp="1i43gxka895s6">: {stateText(data[id])}</span>
          </span>)}
        <span className="ml-auto text-[12px] text-muted group-open:hidden" data-id="element-1883" data-mp="ue97zdt141d7">Details</span>
      </summary>
      <ul className="divide-y divide-line border-t border-line" data-id="element-1884" data-mp="pfen86bi42qg">
        {order.map(id => {
        const s = data[id];
        return <li key={id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-[13px] sm:px-5" data-id="element-1885" data-mp="35u9n3218bqvq">
              <span className="min-w-[12rem] text-ink" data-id="element-1886" data-mp="2s34wsqjr4ay2">{integrationInfo[id].name}</span>
              <span className={`flex-1 ${s.status === 'fout' ? 'text-clay' : 'text-muted'}`} data-id="element-1887" data-mp="2w4gdu7ok7nun">
                {stateText(s)}
                {s.message && <span className="block text-[12px] text-muted" data-id="element-1888" data-mp="3jv40jz34wi19">{s.message}</span>}
              </span>
              {s.status !== 'niet-gekoppeld' && <button type="button" onClick={() => void refresh(id)} disabled={busy !== null} className={`${btnGhost} disabled:opacity-50`} data-id="element-1889" data-mp="3atcu2s0rs76c">
                  <RefreshCwIcon className={`h-3.5 w-3.5 ${busy === id ? 'animate-spin' : ''}`} strokeWidth={1.75} aria-hidden="true" data-id="element-1890" data-mp="3fredje23y8ic" />
                  {busy === id ? 'Ophalen…' : 'Opnieuw ophalen'}
                </button>}
            </li>;
      })}
      </ul>
    </details>;
}